import React from 'react';
import { Box, Paper, Stack, Typography, SxProps, Theme } from '@mui/material';
import { tokens } from '../../theme';

interface SectionCardProps {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  icon?: React.ReactNode;
  /** Akcja w naglowku, np. link „Wszystkie →”. */
  action?: React.ReactNode;
  /** `none` — tresc od krawedzi do krawedzi (tabele, listy). */
  padding?: 'normal' | 'none';
  /** Linia pod naglowkiem. Domyslnie wlaczona, gdy padding = none. */
  divider?: boolean;
  children?: React.ReactNode;
  sx?: SxProps<Theme>;
  contentSx?: SxProps<Theme>;
  id?: string;
}

/** Karta z naglowkiem — podstawowy blok tresci na stronach. */
export const SectionCard: React.FC<SectionCardProps> = ({
  title, subtitle, icon, action, padding = 'normal', divider, children, sx, contentSx, id,
}) => {
  const hasHeader = Boolean(title || subtitle || action);
  const showDivider = divider ?? padding === 'none';
  return (
    <Paper id={id} sx={{ overflow: 'hidden', ...((sx as object) || {}) }}>
      {hasHeader && (
        <Stack
          direction="row"
          alignItems="center"
          gap={1.5}
          sx={{
            px: { xs: 2, md: 2.5 },
            pt: { xs: 1.75, md: 2 },
            pb: showDivider ? { xs: 1.75, md: 2 } : 0,
            borderBottom: showDivider ? `1px solid ${tokens.line}` : 'none',
          }}
        >
          {icon && (
            <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: tokens.plumSoft, color: tokens.plum, display: 'grid', placeItems: 'center', flexShrink: 0, '& .MuiSvgIcon-root': { fontSize: 20 } }}>
              {icon}
            </Box>
          )}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {title && <Typography component="h2" sx={{ fontSize: '1.0625rem', fontWeight: 700, lineHeight: 1.35 }}>{title}</Typography>}
            {subtitle && <Typography component="div" sx={{ fontSize: '0.84375rem', color: tokens.muted, mt: 0.25 }}>{subtitle}</Typography>}
          </Box>
          {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
        </Stack>
      )}
      <Box
        sx={{
          ...(padding === 'normal' ? { px: { xs: 2, md: 2.5 }, pb: { xs: 2, md: 2.5 }, pt: hasHeader && !showDivider ? 1.75 : { xs: 2, md: 2.5 } } : {}),
          ...((contentSx as object) || {}),
        }}
      >
        {children}
      </Box>
    </Paper>
  );
};

export default SectionCard;
